//#region imports
import React, { useMemo, useState, useEffect, useRef } from 'react';

// colors
import * as TaskColors from "../../utils/colors";
import { Colors, Spacing } from "_styles";

// icons
import { FaPlane, FaWalking } from 'react-icons/fa';
import { GoShield } from "react-icons/go";

// custom components
import JobStatus from '../job-card/job-status';

// firestore — RC session reports (one-time fetch per card mount)
import { firestore } from "_utils/firebase-app";
import { collection, query, where, limit, getDocs } from "firebase/firestore";

// bootstrap overlay
import { Overlay, Popover } from 'react-bootstrap';

//#endregion

// ── RC Report Beacon ──────────────────────────────────────────────────────────
// Derives bookingId from the task, fetches remotecheckin_sessions once on mount,
// and shows a pulsing red dot if the passenger has filed any reports.
// Hovering the dot shows the reports in a Bootstrap Popover.
const RCReportBeacon = ({ job }) => {
    const [reports, setReports] = useState([]);
    const [show, setShow]       = useState(false);
    const targetRef             = useRef(null);

    // Derive bookingId the same way crud-remotecheckin does
    const bookingId = useMemo(() => {
        if (job?.originalBookingId) return job.originalBookingId;
        if (job?._id?.includes('_IATA_')) return job._id.split('_IATA_')[0];
        return null;
    }, [job?._id, job?.originalBookingId]);

    useEffect(() => {
        if (!bookingId) return;
        let cancelled = false;
        getDocs(
            query(collection(firestore, 'remotecheckin_sessions'),
                  where('bookingId', '==', bookingId), limit(1))
        ).then(snap => {
            if (cancelled || snap.empty) return;
            const r = snap.docs[0].data().reports;
            if (Array.isArray(r) && r.length > 0) setReports(r);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [bookingId]);

    if (!reports.length) return null;

    const OPTION_LABEL = {
        not_collected:    'Not collected',
        no_longer_needed: 'No longer needed',
        other:            'Other',
    };

    return (
        <>
            <span
                ref={targetRef}
                onMouseEnter={() => setShow(true)}
                onMouseLeave={() => setShow(false)}
                style={{
                    display: 'inline-block',
                    width: 8, height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#ef4444',
                    boxShadow: '0 0 0 0 rgba(239,68,68,0.7)',
                    animation: 'rc-beacon-pulse 1.4s infinite',
                    cursor: 'pointer',
                    flexShrink: 0,
                }}
                title="Passenger reports"
            />
            <Overlay target={targetRef.current} show={show} placement="top">
                <Popover style={{ minWidth: 200, maxWidth: 260, fontSize: 12 }}>
                    <Popover.Header style={{ backgroundColor: '#ef4444', color: '#fff', fontWeight: 700, fontSize: 12 }}>
                        Passenger Reports ({reports.length})
                    </Popover.Header>
                    <Popover.Body style={{ padding: '8px 10px' }}>
                        {reports.map((r, i) => (
                            <div key={i} style={{ marginBottom: i < reports.length - 1 ? 6 : 0 }}>
                                <span style={{ fontWeight: 600, color: '#ef4444' }}>
                                    {OPTION_LABEL[r.option] || r.option}
                                </span>
                                {r.note && <div style={{ color: '#374151', marginTop: 2 }}>{r.note}</div>}
                            </div>
                        ))}
                    </Popover.Body>
                </Popover>
            </Overlay>
        </>
    );
};

const CardBottom = (props) => {

    // function to get background color of card depending on flight type
    const backgroundColor = useMemo(() => {

        // check if gate relocation
        if (props?.job?.isGateRelocation) return TaskColors.gateRelocationTask;

        if (props?.job?.isALTPickUp) return 'purple';

        /// ramp task
        if (props?.job?.jobType === "ramp-task" || props?.job?.combinedRampBooking || props?.isRampTask) {
            return TaskColors.combinedRampBooking
        }
        if (props?.job?.jobType === "boarding-task" || props?.job?.combinedBooking || props?.isBoardingFlight) {
            return TaskColors.combinedBooking
        }

        // arrival task. flight task, grouped task 
        if (props?.userIata === props?.job?.flightConfirmationDetails?.destination || props?.userIata === props?.job?.flightInformation?.arrivalAirport) return Colors.ARRIVALS_GREEN;

        // departure task, boarding task, grouped task 
        if (props?.userIata === props?.job?.flightConfirmationDetails?.origin || props?.userIata === props?.job?.flightInformation?.departureAirport) {
            return props?.job?.combinedBooking || props?.job?.isBoardingBooking ? TaskColors.combinedBooking : Colors.PRM_LIGHT_BLUE;
        };

        return 'lightgrey';

    }, [
        props?.job?.isALTPickUp,
        props?.flightType,
        props?.job?.combinedBooking,
        props?.job?.combinedRampBooking,
        props?.job?.isGateRelocation
    ]);

    return (
        <div style={{ backgroundColor, margin: 0, padding: 0, flex: 1, width: '100%' }}>

            <div style={{ display: "flex", flex: 1, justifyContent: "space-between", padding: Spacing.SCALE_2 }}>

                {/* flight number + iata code */}
                <div style={{ flex: 1.5, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: Spacing.SCALE_4, marginLeft: Spacing.SCALE_2 }}>

                    {/* flight number */}
                    <p style={{ margin: "0px 0", textAlign: "center", fontSize: 10, fontWeight: '600', color: 'white' }} data-tippy-content="Flight Number">
                        {props?.displayFlightNumber}
                    </p>

                    {/* plane icon */}
                    <FaPlane size={11} color="white" />

                    {/* iata code */}
                    <p style={{ margin: "0px 0", textAlign: "center", fontSize: 10, fontWeight: '600', color: 'white' }}>
                        {props?.iata}
                    </p>

                    {/* third state flight icon */}
                    {
                        props?.job?.flightInformation?.thirdStateFlight && <div className='d-flex align-items-center'>
                            <GoShield
                                color={"White"}
                                size={14}
                                data-tippy-content="Third State IATA"
                            />
                        </div>
                    }

                </div>

                {/* RC report beacon + status */}
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: Spacing.SCALE_4 }}>

                    <RCReportBeacon job={props?.job} />

                    {/* self-assisted icon or status */}
                    {props?.job?.preboardAssistanceRequired === false ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: Spacing.SCALE_2, backgroundColor: 'white', borderRadius: 2, paddingLeft: Spacing.SCALE_2, paddingRight: Spacing.SCALE_2 }} data-tippy-content={`Self-assisted passenger${props?.job?.boardingPickupLocation?.name ? ` - Pickup: ${props.job.boardingPickupLocation.name}` : ''}`}>
                            <FaWalking size={14} color="#666" />
                        </div>
                    ) : (
                        <JobStatus job={props?.job} fromLocationPanel={props?.fromLocationPanel} userIata={props?.userIata} />
                    )}

                </div>

            </div>

        </div>
    );
};

export default React.memo(CardBottom, (prev, next) => (
    prev.displayFlightNumber === next.displayFlightNumber &&
    prev.iata === next.iata &&
    prev.gateInformation === next.gateInformation &&
    prev.standInformation === next.standInformation &&
    prev.flightType === next.flightType &&
    prev.job === next.job &&
    prev.job?.preboardAssistanceRequired === next.job?.preboardAssistanceRequired &&
    prev.job?.boardingPickupLocation?.id === next.job?.boardingPickupLocation?.id
));